import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { Send, Hash, Clock } from 'lucide-react';
// Import date-fns for easy formatting (npm install date-fns)
import { format, isToday, isYesterday } from 'date-fns';

const socket = io('http://localhost:5000');

const ChatView = ({ user }) => {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const scrollRef = useRef();
    const notificationSound = useRef(new Audio('https://assets.mixkit.co/active_storage/sfx/2354/2354-preview.mp3'));

    useEffect(() => {
        axios.get('http://localhost:5000/api/messages').then(res => setMessages(res.data));

        socket.on('receiveMessage', (message) => {
            setMessages((prev) => [...prev, message]);
            if (user.role === 'Admin' && message.sender_id !== user.id) {
                playNotification();
            }
        });

        return () => socket.off('receiveMessage');
    }, [user.id, user.role]);

    const playNotification = () => {
        notificationSound.current.play().catch(() => {});
    };

    useEffect(() => {
        scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = (e) => {
        e.preventDefault();
        if (!newMessage.trim()) return;

        const messageData = {
            sender_id: user.id,
            sender_name: user.name,
            text: newMessage,
            // MUST BE ISO STRING FOR DATABASE COMPATIBILITY
            created_at: new Date().toISOString() 
        };

        socket.emit('sendMessage', messageData);
        setNewMessage('');
    };

    // Helper to render date dividers (Today, Yesterday, or Date)
   const renderDateDivider = (dateString, index) => {
    // Safety Check: If there's no date, don't show a divider
    if (!dateString) return null;

    const date = new Date(dateString);
    
    // Check if the date is actually valid
    if (isNaN(date.getTime())) return null;

    const prevMsgDate = index > 0 && messages[index - 1].created_at 
        ? new Date(messages[index - 1].created_at) 
        : null;

    // Only show divider if date changed
    if (prevMsgDate && !isNaN(prevMsgDate.getTime()) && date.toDateString() === prevMsgDate.toDateString()) {
        return null;
    }

    let label = format(date, 'MMMM do, yyyy');
    if (isToday(date)) label = "Today";
    if (isYesterday(date)) label = "Yesterday";

    return (
        <div style={{ display: 'flex', alignItems: 'center', margin: '20px 0', gap: '15px' }}>
            <div style={{ flex: 1, height: '1px', background: 'rgba(0,0,0,0.05)' }} />
            <span style={{ fontSize: '11px', fontWeight: '600', color: '#000000', textTransform: 'uppercase' }}>
                {label}
            </span>
            <div style={{ flex: 1, height: '1px', background: 'rgba(0,0,0,0.05)' }} />
        </div>
    );
};

    return (
        <div className="chat-container" style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#cab6f0', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            
            {/* Header */}
            <div className="chat-header" style={{ padding: '16px', background: 'white', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ background: '#eef2ff', padding: '8px', borderRadius: '8px' }}>
                        <Hash size={20} color="#6366f1" />
                    </div>
                    <div>
                        <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>Staff General Chat</h3>
                        <span style={{ fontSize: '11px', color: '#64748b' }}>Real-time coordination</span>
                    </div>
                </div>
                {user.role === 'Admin' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', background: '#ecfdf5', padding: '4px 10px', borderRadius: '20px' }}>
                        <div style={{ width: '6px', height: '6px', background: '#10b981', borderRadius: '50%' }} />
                        <span style={{ fontSize: '10px', color: '#065f46', fontWeight: '600' }}>Admin Alerts On</span>
                    </div>
                )}
            </div>

            {/* Messages Area */}
            <div className="messages-area" style={{ flex: 1, overflowY: 'auto', padding: '20px', background: '#e3daff' }}>
                {messages.map((msg, i) => (
                    <React.Fragment key={i}>
                        {renderDateDivider(msg.created_at, i)}
                        
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: msg.sender_id === user.id ? 'flex-end' : 'flex-start', 
                            color: '#000000',
                            marginBottom: '16px' 
                        }}>
                            {/* SENDER NAME & TIME ROW */}
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px', 
                                marginBottom: '4px',
                                color: '#000000',
                                flexDirection: msg.sender_id === user.id ? 'row-reverse' : 'row' // Aligns time correctly for both sides
                            }}>
                                {/* Only show name if it's someone else */}
                                {msg.sender_id !== user.id && (
                                    <span style={{ 
                                        fontSize: '12px', 
                                        fontWeight: '700', 
                                        color: '#000000',
                                        marginLeft: '4px' 
                                    }}>
                                        {msg.sender_name}
                                    </span>
                                )}
                                
                                {/* Modern Timestamp */}
                                <span style={{ fontSize: '10px', color: '#000000', fontWeight: '500' }}>
                                    {msg.created_at && !isNaN(new Date(msg.created_at).getTime()) 
                                        ? format(new Date(msg.created_at), 'p') 
                                        : 'Just now'}
                                </span>
                            </div>

                            {/* MESSAGE BUBBLE */}
                            <div style={{ 
                                padding: '10px 16px', 
                                borderRadius: msg.sender_id === user.id ? '16px 16px 2px 16px' : '2px 16px 16px 16px', 
                                maxWidth: '75%',
                                background: msg.sender_id === user.id ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' : 'white',
                                color: msg.sender_id === user.id ? 'white' : '#1e293b',
                                boxShadow: msg.sender_id === user.id ? '0 4px 12px rgba(99, 102, 241, 0.2)' : '0 2px 5px rgba(0,0,0,0.05)',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                position: 'relative'
                            }}>
                                {msg.text}
                            </div>
                        </div>
                    </React.Fragment>
                ))}
                <div ref={scrollRef} />
            </div>

            {/* Footer Input */}
            <form onSubmit={handleSend} style={{ padding: '20px', background: 'white', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                    <input 
                        type="text" 
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message here..."
                        style={{ width: '90%', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#f8fafc', outline: 'none', transition: 'border 0.2s' }}
                    />
                </div>
                <button type="submit" style={{ width: '45px', height: '45px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'transform 0.1s' }} onMouseDown={e => e.currentTarget.style.transform = 'scale(0.95)'} onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}>
                    <Send size={18} />
                </button>
            </form>
        </div>
    );
};

export default ChatView;